import { levelOf, type NodeType } from '../diagrams/node-types';

/**
 * Превью схемы из списка (API `DiagramPreview`): до 40 карточек верхних уровней и связи парами
 * индексов. Связи — массивами чисел: так их выводит и ответ списка, и контракт.
 */
export interface DiagramPreview {
  nodeCount: number;
  nodes: ReadonlyArray<{ type: NodeType; sortKey: string }>;
  links: ReadonlyArray<ReadonlyArray<number>>;
}

export const THUMB = {
  W: 28, // карточка
  H: 18,
  GX: 8, // между карточками в ряду
  GY: 18, // между уровнями
};
const STEP = THUMB.W + THUMB.GX;

export interface ThumbnailLayout {
  /** Левый верхний угол карточки по её индексу в превью. */
  positions: ReadonlyArray<{ x: number; y: number }>;
  width: number;
  height: number;
}

/**
 * Раскладка мини-превью (docs/saas/screens/блок-схемы.md): ряды — уровни типов, порядок в ряду —
 * как на холсте (`sortKey`), положение — по связям, как у ELK: потомок под родителем, родитель
 * над серединой потомков. Упрощённый Sugiyama: два прохода (вниз по родителям, вверх по
 * потомкам) с разрешением наложений. Без ELK — превью рисуется мгновенно для десятков схем.
 */
export function layoutThumbnail(preview: DiagramPreview): ThumbnailLayout {
  const { W, H, GY } = THUMB;
  const byLevel = new Map<number, number[]>();
  preview.nodes.forEach((node, index) => {
    const level = levelOf(node.type);
    byLevel.set(level, [...(byLevel.get(level) ?? []), index]);
  });
  const levels = [...byLevel.keys()].sort((a, b) => a - b);
  const keyOf = (index: number) => preview.nodes[index]?.sortKey ?? '';
  const rows = levels.map((level) =>
    (byLevel.get(level) ?? []).toSorted((a, b) =>
      keyOf(a) < keyOf(b) ? -1 : keyOf(a) > keyOf(b) ? 1 : a - b,
    ),
  );
  const rowOf = new Map<number, number>();
  rows.forEach((row, r) => row.forEach((index) => rowOf.set(index, r)));

  const parents = new Map<number, number[]>();
  const children = new Map<number, number[]>();
  for (const [source, target] of preview.links) {
    if (source === undefined || target === undefined) continue;
    if (!rowOf.has(source) || !rowOf.has(target)) continue;
    parents.set(target, [...(parents.get(target) ?? []), source]);
    children.set(source, [...(children.get(source) ?? []), target]);
  }

  // Старт: порядок с холста, ряды слева направо.
  const x = new Map<number, number>();
  rows.forEach((row) => row.forEach((index, i) => x.set(index, i * STEP)));

  const mean = (ids: number[] | undefined, r: number, above: boolean): number | undefined => {
    const xs = (ids ?? [])
      .filter((id) => (above ? (rowOf.get(id) ?? 0) < r : (rowOf.get(id) ?? 0) > r))
      .map((id) => x.get(id) ?? 0);
    return xs.length > 0 ? xs.reduce((sum, value) => sum + value, 0) / xs.length : undefined;
  };

  /**
   * Ряд заново, порядок в ряду не меняется (его выбрал `sortKey`): узел встаёт на желаемый x
   * (среднее по родителям/потомкам), если не наезжает на предыдущий, иначе — вплотную за ним.
   * Затем ряд сдвигается целиком, чтобы узлы с ориентиром в среднем стояли где хотели.
   */
  const placeRow = (r: number, above: boolean) => {
    const row = rows[r] ?? [];
    const desired = new Map<number, number | undefined>();
    row.forEach((index) =>
      desired.set(index, mean(above ? parents.get(index) : children.get(index), r, above)),
    );
    const anchored = row.filter((index) => desired.get(index) !== undefined);
    if (anchored.length === 0) return;
    let cursor = Number.NEGATIVE_INFINITY;
    for (const index of row) {
      // Узел без ориентира в начале ряда остаётся на месте, а не уезжает в -∞.
      const want = desired.get(index) ?? (Number.isFinite(cursor) ? cursor : (x.get(index) ?? 0));
      const px = Math.max(want, cursor);
      x.set(index, px);
      cursor = px + STEP;
    }
    const delta =
      anchored.reduce((sum, index) => sum + ((desired.get(index) ?? 0) - (x.get(index) ?? 0)), 0) /
      anchored.length;
    if (delta !== 0) row.forEach((index) => x.set(index, (x.get(index) ?? 0) + delta));
  };

  for (let iteration = 0; iteration < 2; iteration += 1) {
    for (let r = 1; r < rows.length; r += 1) placeRow(r, true);
    for (let r = rows.length - 2; r >= 0; r -= 1) placeRow(r, false);
  }

  const xs = [...x.values()];
  const minX = xs.length > 0 ? Math.min(...xs) : 0;
  const maxX = xs.length > 0 ? Math.max(...xs) : 0;
  const positions = preview.nodes.map((_, index) => ({
    x: (x.get(index) ?? 0) - minX,
    y: (rowOf.get(index) ?? 0) * (H + GY),
  }));
  return {
    positions,
    width: xs.length > 0 ? maxX - minX + W : 0,
    height: rows.length > 0 ? rows.length * (H + GY) - GY : 0,
  };
}
